import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { fetchBaasAccountOpeningEvents } from "../../../../../Services/baasAccountsListApi";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const EVENT_LABEL_KEYS = {
  PROPOSAL_CREATED: "baas.accounts.openingEvents.events.proposalCreated",
  PROPOSAL_WAITING_AUTOMATIC_ANALYSIS:
    "baas.accounts.openingEvents.events.waitingAnalysis",
  AWAITING_AUTOMATIC: "baas.accounts.openingEvents.events.waitingAnalysis",
  AWAITING_AUTOMATIC_ANALYSIS: "baas.accounts.openingEvents.events.waitingAnalysis",
  PROPOSAL_WAITING_MANUAL_ANALYSIS:
    "baas.accounts.openingEvents.events.waitingManualAnalysis",
  AWAITING_MANUAL: "baas.accounts.openingEvents.events.waitingManualAnalysis",
  AWAITING_MANUAL_ANALYSIS:
    "baas.accounts.openingEvents.events.waitingManualAnalysis",
  PROPOSAL_AUTOMATIC_ANALYSIS_RESULT:
    "baas.accounts.openingEvents.events.analysisResult",
  PROPOSAL_MANUAL_ANALYSIS_RESULT:
    "baas.accounts.openingEvents.events.manualAnalysisResult",
  PROPOSAL_APPROVED_MANUAL: "baas.accounts.openingEvents.events.approvedManual",
  PROPOSAL_APPROVED_MANUALLY: "baas.accounts.openingEvents.events.approvedManual",
  APPROVED_MANUAL: "baas.accounts.openingEvents.events.approvedManual",
  PROPOSAL_APPROVED_AUTOMATIC:
    "baas.accounts.openingEvents.events.approvedAutomatic",
  PROPOSAL_APPROVED_AUTOMATICALLY:
    "baas.accounts.openingEvents.events.approvedAutomatic",
  APPROVED_AUTOMATIC: "baas.accounts.openingEvents.events.approvedAutomatic",
  PROPOSAL_REJECTED_MANUAL: "baas.accounts.openingEvents.events.rejectedManual",
  PROPOSAL_REJECTED_MANUALLY: "baas.accounts.openingEvents.events.rejectedManual",
  REJECTED_MANUAL: "baas.accounts.openingEvents.events.rejectedManual",
  ACCOUNT_CREATED: "baas.accounts.openingEvents.events.accountCreated",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "short",
      timeStyle: "medium",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const resolveEventLabelKey = (eventName) => {
  const token = String(eventName || "").trim().toUpperCase();
  if (!token) return null;
  if (EVENT_LABEL_KEYS[token]) return EVENT_LABEL_KEYS[token];

  const withoutProposal = token.startsWith("PROPOSAL_") ? token.slice("PROPOSAL_".length) : token;
  if (EVENT_LABEL_KEYS[withoutProposal]) return EVENT_LABEL_KEYS[withoutProposal];

  return (
    Object.entries(EVENT_LABEL_KEYS).find(
      ([code]) => token === code || token.endsWith(`_${code}`) || token.endsWith(code),
    )?.[1] ?? null
  );
};

const eventLabel = (eventName) => {
  const key = resolveEventLabelKey(eventName);
  if (!key) return eventName || "—";
  const label = intl.get(key);
  return label && label !== key ? label : eventName;
};

export const BaasAccountOpeningEventsPanel = ({ instance, accountId, inModal = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!instance || !accountId) {
      setEvents([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    fetchBaasAccountOpeningEvents(instance, accountId)
      .then((result) => {
        if (cancelled) return;
        const list = [...(result?.events || [])].sort((a, b) => {
          const ta = Date.parse(a?.occurredAt || "") || 0;
          const tb = Date.parse(b?.occurredAt || "") || 0;
          return ta - tb;
        });
        setEvents(list);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instance, accountId]);

  return (
    <section>
      {inModal ? null : (
        <p className="dashboard-section-label mb-3">
          {intl.get("baas.accounts.openingEvents.title")}
        </p>
      )}
      {loading ? (
        <div className={`${BORDER_CARD} app-empty-state`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.openingEvents.loading")}
          </p>
        </div>
      ) : events.length === 0 ? (
        <div className={`${BORDER_CARD} app-empty-state`}>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.openingEvents.empty")}
          </p>
        </div>
      ) : (
        <ol className={`${BORDER_CARD} divide-y divide-gray-100 dark:divide-brand-dark-border`}>
          {events.map((item, index) => (
            <li
              key={`${item.event}-${item.occurredAt}-${item.analysisType || index}`}
              className="px-4 py-3 sm:px-5"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {eventLabel(item.event)}
                </p>
                <time className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDateTime(item.occurredAt)}
                </time>
              </div>
              <dl className="mt-2 grid gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-400 sm:grid-cols-2">
                {item.status ? (
                  <>
                    <dt>{intl.get("baas.accounts.openingEvents.status")}</dt>
                    <dd className="font-medium text-brand-dark dark:text-gray-200">{item.status}</dd>
                  </>
                ) : null}
                {item.analysisType ? (
                  <>
                    <dt>{intl.get("baas.accounts.openingEvents.analysisType")}</dt>
                    <dd className="font-medium text-brand-dark dark:text-gray-200">
                      {item.analysisType}
                    </dd>
                  </>
                ) : null}
                {item.proposalId ? (
                  <>
                    <dt>{intl.get("baas.accounts.openingEvents.proposalId")}</dt>
                    <dd className="font-mono text-brand-dark dark:text-gray-200">{item.proposalId}</dd>
                  </>
                ) : null}
                {item.document ? (
                  <>
                    <dt>{intl.get("baas.accounts.openingEvents.document")}</dt>
                    <dd className="font-mono text-brand-dark dark:text-gray-200">{item.document}</dd>
                  </>
                ) : null}
              </dl>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default BaasAccountOpeningEventsPanel;
